import { Component, OnInit, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { BaseComponent } from '../../../base/base.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { Certifications } from '../../../../models/api/view/claim/details/certifications/certifications';

@Component({
  selector: 'app-certifications',
  imports: [
    TableComponent,
    ColumnComponent,
    DetailPanelComponent,
    DetailItemComponent,
    CardComponent,
    PageHeaderComponent,
    DatePipe,
  ],
  template: `
    <app-page-header label="Certifications" />
    <app-table
      [dataSource]="certifications"
      [pageSize]="5"
      recordLabel="Certification"
      [rowExpandLabel]="rowExpandLabel"
      [expandable]="true"
      dataKey="id"
      [stripedRows]="false"
      [globalFilterFields]="[
        'relationship',
        'certificationDateRange',
        'certificationStatus',
        'reason',
        'intermittent',
        'dateCreated',
      ]"
    >
      <app-column label="Relationship" name="relationship" />
      <app-column name="beginDate" label="Certification Dates" format="d">
        <ng-template let-data #value>
          {{ data.beginDate | date: 'M/d/yyyy' }} -
          {{ data.endDate | date: 'M/d/yyyy' }}
        </ng-template>
      </app-column>
      <app-column name="certificationStatus" label="Status"> </app-column>
      <app-column label="Reason" name="reason">
        <ng-template let-data #value>
          <div class="tw-text-wrap">
            {{ data.reason }}
          </div>
        </ng-template>
      </app-column>
      <app-column label="Intermittent" name="intermittent" />
      <app-column label="Created" name="dateCreated" format="d" />

      <ng-template #expand let-data>
        <div class="tw-flex tw-gap-8">
          <div class="tw-basis-1/3">
            <app-card label="Certification">
              <app-detail-panel [dataSource]="data" [showEmpty]="false">
                <app-detail-item
                  name="certificationStatus"
                  label="Status"
                />
                <app-detail-item
                  name="certificationSubStatus"
                  label="Substatus"
                />
                <app-detail-item
                  name="beginDate"
                  label="Begin date"
                  format="d"
                />
                <app-detail-item name="endDate" label="End date" format="d" />
                <app-detail-item
                  name="dateCertificationSent"
                  label="Date sent"
                  format="d"
                />
                <app-detail-item
                  name="dateCertificationReceived"
                  label="Date received"
                  format="d"
                />
                <app-detail-item
                  name="dateCertificationReviewed"
                  label="Date reviewed"
                  format="d"
                />
                <app-detail-item
                  name="dateCertificationDue"
                  label="Date due"
                  format="d"
                  [showEmpty]="false"
                />
              </app-detail-panel>
            </app-card>
          </div>
          @if (hasClarificationData(data)) {
            <div class="tw-basis-1/3">
              <app-card label="Clarification">
                <app-detail-panel [dataSource]="data">
                  <app-detail-item name="clarificationType" label="Type" />
                  <app-detail-item name="clarificationReason" label="Reason" />
                  <app-detail-item
                    name="dateClarificationSent"
                    label="Date Sent"
                    format="d"
                  />
                  <app-detail-item
                    name="dateClarificationReceived"
                    label="Date Received"
                    format="d"
                  />
                  <app-detail-item
                    name="dateClarificationReviewed"
                    label="Date Reviewed"
                    format="d"
                  />
                  <app-detail-item
                    name="dateClarificationDue"
                    label="Date Due"
                    format="d"
                  />
                </app-detail-panel>
              </app-card>
            </div>
          }
          <div class="tw-basis-1/3">
            <app-card label="Intermittent Absence Frequency">
              <app-detail-panel [dataSource]="data">
                <app-detail-item
                  name="incapacityFrequency"
                  label="Incapacity/Care"
                  labelCase="none"
                />
                <app-detail-item
                  name="treatmentFrequency"
                  label="Treatment/Appointments"
                  labelCase="none"
                />
              </app-detail-panel>
            </app-card>
          </div>
        </div>
      </ng-template>
    </app-table>
  `,
  styles: ``,
})
export class CertificationsComponent extends BaseComponent implements OnInit {
  private datePipe = inject(DatePipe);

  claim: ClaimRow;
  showClarification = false;
  certifications: Certifications[] = [];

  async ngOnInit() {
    this.claim = this.view.currentClaim();
    const rows = await this.api.certifications.getCertificates(
      this.claim.recordSource,
      this.claim.recordId,
    );
    // Add combined date range string so global filter matches "begin - end" (e.g. "3/26/2022 - 9/22/2022")
    this.certifications = rows.map((cert) => ({
      ...cert,
      certificationDateRange: this.getCertificationDateRange(cert),
    }));

    this.view.hideLoading();
  }

  public rowExpandLabel = (cert: Certifications): string => {
    const begin = this.formatDate(cert?.beginDate);
    const end = this.formatDate(cert?.endDate);
    const range = begin && end ? `${begin} - ${end}` : begin || end || 'dates';

    return `certification for ${range}`;
  };

  private formatDate(value: any): string | null {
    return value ? this.datePipe.transform(value, 'MM/dd/yyyy') : null;
  }

  /** Combined begin - end date in same format as column display (M/d/yyyy) for global filter. */
  private getCertificationDateRange(cert: Certifications): string {
    const begin = cert?.beginDate
      ? this.datePipe.transform(cert.beginDate, 'M/d/yyyy')
      : '';
    const end = cert?.endDate
      ? this.datePipe.transform(cert.endDate, 'M/d/yyyy')
      : '';
    return begin && end ? `${begin} - ${end}` : begin || end || '';
  }

  /**
   * Check if a specific certification row has clarification data
   * @param cert - Individual certification object
   * @returns boolean indicating if clarification section should be shown for this row
   */
  hasClarificationData(cert: Certifications): boolean {
    return (
      (cert.clarificationType != null && cert.clarificationType !== '') ||
      (cert.clarificationReason != null && cert.clarificationReason !== '') ||
      cert.dateClarificationSent != null ||
      cert.dateClarificationReceived != null ||
      cert.dateClarificationReviewed != null ||
      cert.dateClarificationDue != null
    );
  }
}
